import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { ContributionType } from "@/data/contribution-types";
import {
  Sparkles,
  Bug,
  FileText,
  Workflow,
  TestTube,
  Shield,
  Accessibility,
  RefreshCw,
  Zap,
} from "lucide-react";
import React from "react";

/*
 * AAA-compliant color pairings (≥ 7:1):
 *   Light: -900 text on -100 bg (≥ 8:1 for all hues)
 *   Dark:  -300 text on -900/30 bg (≥ 7:1 for all hues; -300 chosen over -400
 *          for red/indigo where -400 falls below 7:1)
 */
const TYPE_CONFIG: Record<
  ContributionType,
  {
    icon: React.ComponentType<{ className?: string }>;
    className: string;
  }
> = {
  [ContributionType.Feature]: {
    icon: Sparkles,
    className:
      "bg-emerald-100 text-emerald-900 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-300 dark:border-transparent",
  },
  [ContributionType.Bugfix]: {
    icon: Bug,
    className:
      "bg-red-100 text-red-900 border-red-200 dark:bg-red-900/30 dark:text-red-300 dark:border-transparent",
  },
  [ContributionType.Docs]: {
    icon: FileText,
    className:
      "bg-blue-100 text-blue-900 border-blue-200 dark:bg-blue-900/30 dark:text-blue-300 dark:border-transparent",
  },
  [ContributionType.CI]: {
    icon: Workflow,
    className:
      "bg-purple-100 text-purple-900 border-purple-200 dark:bg-purple-900/30 dark:text-purple-300 dark:border-transparent",
  },
  [ContributionType.Tests]: {
    icon: TestTube,
    className:
      "bg-yellow-100 text-yellow-900 border-yellow-200 dark:bg-yellow-900/30 dark:text-yellow-300 dark:border-transparent",
  },
  [ContributionType.Security]: {
    icon: Shield,
    className:
      "bg-orange-100 text-orange-900 border-orange-200 dark:bg-orange-900/30 dark:text-orange-300 dark:border-transparent",
  },
  [ContributionType.Accessibility]: {
    icon: Accessibility,
    className:
      "bg-pink-100 text-pink-900 border-pink-200 dark:bg-pink-900/30 dark:text-pink-300 dark:border-transparent",
  },
  [ContributionType.Refactoring]: {
    icon: RefreshCw,
    className:
      "bg-indigo-100 text-indigo-900 border-indigo-200 dark:bg-indigo-900/30 dark:text-indigo-300 dark:border-transparent",
  },
  [ContributionType.Performance]: {
    icon: Zap,
    className:
      "bg-cyan-100 text-cyan-900 border-cyan-200 dark:bg-cyan-900/30 dark:text-cyan-300 dark:border-transparent",
  },
};

type ContributionTypeBadgeProps = {
  type: ContributionType;
  showIcon?: boolean;
  className?: string;
};

export function ContributionTypeBadge({
  type,
  showIcon = true,
  className,
}: ContributionTypeBadgeProps) {
  const config = TYPE_CONFIG[type];
  const Icon = config.icon;

  return (
    <Badge
      variant="outline"
      className={cn(
        "border text-base font-semibold",
        config.className,
        className,
      )}
      data-testid="contribution-type-badge"
    >
      {showIcon && <Icon className="mr-1 size-4" aria-hidden="true" />}
      {type}
    </Badge>
  );
}
